import React, { FC, useContext, useMemo } from 'react';
import { OptionsWithInput } from 'shared/components/OptionsWithInput/OptionsWithInput';
import { MatchAccordionDescription } from 'modules/matches/components/MatchAccordionDescription/MatchAccordionDescription';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Accordion } from 'shared/components/Accordion';
import { Box, Typography } from '@mui/material';
import { getEntryAmountOptions, getOklpEntryAmountOptions } from 'modules/matches/constants';
import { walletSelector } from 'modules/payments/store/wallet/selectors';
import {
  HIGH_FORMAT_CURRENCY_PRECISION,
  calculatePlatformFeeFromPrimaryAmount,
  formatCrypto,
} from 'shared/utils';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { MatchFormFields } from 'modules/matches/interfaces/forms';
import { GameCurrencyWithFee } from 'modules/game-payments/components';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';

interface MatchSelectionFormProps {
  onAccordionClick: () => void;
  isExpanded: boolean;
}

export const MatchPaymentForm: FC<MatchSelectionFormProps> = ({ onAccordionClick, isExpanded }) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<MatchFormFields>();

  const entryFee = watch('entryFee');

  const exchangeRate = useSelector(walletSelector.exchangeRate);
  const { paymentConfig, platformFeePercentage } = useContext(GameActivityPaymentContext);

  const platformFee = useMemo(
    () =>
      calculatePlatformFeeFromPrimaryAmount(
        entryFee,
        paymentConfig.exchangeRate,
        platformFeePercentage
      ),
    [entryFee, platformFeePercentage, paymentConfig.exchangeRate]
  );

  const formattedPlatformFee = formatCrypto(
    platformFee,
    paymentConfig.platformFeeCurrency,
    HIGH_FORMAT_CURRENCY_PRECISION
  );

  const entryFeeLabel = useMemo(() => {
    if (errors.entryFee?.message) return '(errors)';

    return <GameCurrencyWithFee amount={entryFee} />;
  }, [entryFee, errors.entryFee?.message]);

  const entryAmountOptions = useMemo(
    () =>
      MultiCurrencyConfig.isEnabledForMatches()
        ? getEntryAmountOptions(paymentConfig.exchangeRate, platformFeePercentage)
        : getOklpEntryAmountOptions(exchangeRate),
    [exchangeRate, platformFeePercentage, paymentConfig.exchangeRate]
  );

  const customEntryFeeSuffix = useMemo(() => {
    if (!MultiCurrencyConfig.isEnabledForMatches()) return undefined;

    return ` ${paymentConfig.mainCurrency} +${formattedPlatformFee}`;
  }, [formattedPlatformFee, paymentConfig.mainCurrency]);

  return (
    <Accordion
      title="Entry Amount"
      description={<MatchAccordionDescription text={<>{entryFeeLabel}</>} />}
      onChange={onAccordionClick}
      expanded={isExpanded}
    >
      <Controller
        control={control}
        name="entryFee"
        render={({ field: { onChange, onBlur, value, ...rest } }) => (
          <>
            <Typography sx={{ mb: 2 }}>Entry Amount per Player</Typography>
            <OptionsWithInput
              options={entryAmountOptions}
              isInputDisabled={false}
              showUsdEquivalentInInput={true}
              inputGrid={{ xs: 6, md: 6 }}
              value={value}
              onSelect={(newValue: number) => {
                onChange(newValue);
                onBlur?.();
              }}
              onBlur={(newValue: number) => {
                onChange(newValue);
                onBlur?.();
              }}
              inputSuffix={customEntryFeeSuffix}
              {...rest}
            />
            <Box mt={2}>
              {errors.entryFee?.message && (
                <ErrorMessage error={errors.entryFee.message} withSidePadding={false} />
              )}
            </Box>
          </>
        )}
      />
    </Accordion>
  );
};
